import firebaseApp, { storage, db } from "services/firebase";

/** Files larger than this are uploaded to Storage first to avoid the inline 8 MB callable limit. */
const MAX_INLINE_BYTES = 8 * 1024 * 1024;

const functions = firebaseApp.functions();

const uploadDocsCallable = functions.httpsCallable("clinicalreferences_uploaddocs", {
  timeout: 300000,
});
const listDocsCallable = functions.httpsCallable("clinicalreferences_listdocs", {
  timeout: 60000,
});
const listAvailableCallable = functions.httpsCallable("clinicalreferences_listavailable", {
  timeout: 60000,
});
const deleteDocsCallable = functions.httpsCallable("clinicalreferences_deletedocs", {
  timeout: 60000,
});
const setPromptAttachmentCallable = functions.httpsCallable(
  "clinicalreferences_setpromptattachment",
  { timeout: 60000 },
);
const retrieveLexicalCallable = functions.httpsCallable(
  "clinicalreferences_retrieve_lexical",
  { timeout: 60000 }
);
const listLibraryCallable = functions.httpsCallable(
  "clinicalreferences_listlibrary",
  { timeout: 60000 }
);
const attachToPromptCallable = functions.httpsCallable(
  "clinicalreferences_attachtoprompt",
  { timeout: 60000 }
);

function normalizePromptId(promptId) {
  return String(promptId || "").trim().toLowerCase();
}

/**
 * Convert a File object to a base64 string (data URI prefix stripped).
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Upload a single PDF file.
 * Files ≤ 8 MB are sent inline (base64). Larger files are staged in Firebase
 * Storage and the callable receives a storagePath instead — matching the backend
 * `storagePath` branch in clinicalreferences_uploaddocs.
 *
 * @param {File} file
 * @param {string} promptId
 * @param {{ source?: string, specialty?: string, documentType?: string }} meta
 * @returns {Promise<Object>} result from the function
 */
export async function uploadDocument(file, promptId, meta = {}, scope = "global") {
  const normalizedPromptId = normalizePromptId(promptId);
  if (!normalizedPromptId) {
    throw new Error("A prompt ID is required to upload clinical references.");
  }

  const metadata = {
    source: meta.source || "",
    specialty: meta.specialty || "",
    documentType: meta.documentType || "reference",
  };

  let payload;

  if (file.size > MAX_INLINE_BYTES) {
    const tmpPath = `clinical-refs-tmp/${normalizedPromptId}/${Date.now()}-${file.name}`;
    const ref = storage.ref(tmpPath);
    await ref.put(file);
    try {
      const result = await uploadDocsCallable({
        storagePath: tmpPath,
        fileName: file.name,
        fileType: file.type || "application/pdf",
        promptId: normalizedPromptId,
        metadata,
        scope,
      });
      payload = result.data;
    } finally {
      ref.delete().catch(() => {});
    }
    return payload;
  }

  const fileBase64 = await fileToBase64(file);
  const result = await uploadDocsCallable({
    fileBase64,
    fileName: file.name,
    fileType: file.type || "application/octet-stream",
    promptId: normalizedPromptId,
    metadata,
    scope,
  });
  return result.data;
}

/**
 * List all clinical reference documents for the current user.
 * @returns {Promise<Array>} array of document metadata objects
 */
/**
 * @param {string} [scope] - "global", "org", or omitted for all visible to caller.
 */
export async function listDocuments(promptId, scope) {
  const normalizedPromptId = normalizePromptId(promptId);
  if (!normalizedPromptId) {
    return [];
  }
  const payload = { promptId: normalizedPromptId };
  if (scope) payload.scope = scope;
  const result = await listDocsCallable(payload);
  return result.data?.documents ?? [];
}

/**
 * List all clinical reference documents uploaded by the caller, annotated for this prompt.
 * @returns {Promise<Array>} array of document metadata objects
 */
export async function listAvailableDocuments(promptId) {
  const normalizedPromptId = normalizePromptId(promptId);
  if (!normalizedPromptId) {
    return [];
  }
  const result = await listAvailableCallable({ promptId: normalizedPromptId });
  return result.data?.documents ?? [];
}

/**
 * Delete one or more clinical reference documents.
 * - detachOnly:true removes the docs from the prompt only; they stay in the library
 *   even when this was their last prompt.
 * - everywhere:true permanently deletes the doc and its Storage file; promptId is
 *   not required (works on orphaned docs too).
 * - Default: detaches when shared with other prompts, deletes otherwise.
 * @param {string|string[]} docIds
 * @param {string|null} promptId
 * @param {{ everywhere?: boolean, detachOnly?: boolean }} [options]
 */
export async function deleteDocument(docIds, promptId, options = {}) {
  const normalizedPromptId = normalizePromptId(promptId);
  const everywhere = options.everywhere === true;
  if (!normalizedPromptId && !everywhere) {
    throw new Error("A prompt ID is required to delete clinical references.");
  }
  const ids = Array.isArray(docIds) ? docIds : [docIds];
  const result = await deleteDocsCallable({
    docIds: ids,
    promptId: normalizedPromptId || null,
    everywhere,
    detachOnly: options.detachOnly === true,
  });
  return result.data;
}

/**
 * List all clinical references visible to the caller (no promptId filter).
 * @param {{ scope?: "global" | "org" }} [options]
 */
export async function listLibrary({ scope } = {}) {
  const payload = scope ? { scope } : {};
  const result = await listLibraryCallable(payload);
  return result.data?.documents ?? [];
}

/**
 * Attach existing clinical references (by ID) to a promptId.
 * @param {string[]} docIds
 * @param {string} promptId
 */
export async function attachToPrompt(docIds, promptId) {
  const normalizedPromptId = normalizePromptId(promptId);
  if (!normalizedPromptId) {
    throw new Error("A prompt ID is required to attach clinical references.");
  }
  const ids = (Array.isArray(docIds) ? docIds : [docIds])
    .map((id) => String(id).trim())
    .filter(Boolean);
  if (!ids.length) {
    return { success: true, attached: [], errors: [] };
  }
  const result = await attachToPromptCallable({
    docIds: ids,
    promptId: normalizedPromptId,
  });
  return result.data;
}

/**
 * Attach/detach an existing clinical reference from one prompt.
 * @param {string} docId
 * @param {string} promptId
 * @param {boolean} attach
 * @returns {Promise<Object>} result from the function
 */
export async function updateDocumentPromptAttachment(docId, promptId, attach) {
  const normalizedPromptId = normalizePromptId(promptId);
  const normalizedDocId = String(docId || "").trim();
  if (!normalizedDocId || !normalizedPromptId) {
    throw new Error("A document ID and prompt ID are required.");
  }

  const result = await setPromptAttachmentCallable({
    docId: normalizedDocId,
    promptId: normalizedPromptId,
    attach: attach === true,
  });
  return result.data;
}

const CLINICAL_REFS_COLLECTION = "chartdmind/clinical_references/documents";

/**
 * Direct Firestore fallback: returns GLOBAL clinical reference docs for a
 * promptId. Used when the lexical-retrieve cloud function is unavailable.
 *
 * Scoped to scope=="global" so the query stays within Firestore rules (which
 * forbid cross-tenant reads). Org-specific refs are served by the primary
 * cloud-function path; this degraded fallback intentionally returns only the
 * global library.
 */
async function fetchClinicalRefsByPromptId(normalizedPromptId) {
  const snap = await db
    .collection(CLINICAL_REFS_COLLECTION)
    .where("promptId", "==", normalizedPromptId)
    .where("scope", "==", "global")
    .get();
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Retrieve relevant clinical references using lexical search.
 * Falls back to a direct Firestore read (all docs for the promptId) if the
 * cloud function is unavailable or crashes.
 * @param {string} conversationText - Conversation or transcript to search against
 * @param {{ limit?: number, promptId?: string }} options
 * @returns {Promise<Array>} array of matching document objects
 */
export async function retrieveClinicalReferences(
  conversationText,
  { limit = 10, promptId } = {}
) {
  const normalizedPromptId = normalizePromptId(promptId);
  if (!normalizedPromptId) {
    return [];
  }
  try {
    const result = await retrieveLexicalCallable({
      conversation: conversationText,
      limit,
      promptId: normalizedPromptId,
    });
    return result.data?.documents ?? [];
  } catch (err) {
    console.warn(
      "[clinicalRefs] lexical function failed, falling back to Firestore direct query:",
      err?.message
    );
    return fetchClinicalRefsByPromptId(normalizedPromptId);
  }
}
